import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, Chapter, MaskedLine } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { PRICING, WEBSITE_INCLUDES, GROWTH_INCLUDES } from "../data/pricing";
import { faqSchema, ORG_ID } from "../data/schema";
import { waLink } from "../lib/whatsapp";
import { track } from "../lib/analytics";

const FAQS = [
  {
    q: "How much does a business website cost?",
    a: `Grotech Web websites start from ${PRICING.IN.website} in India, ${PRICING.US.website} in the USA and ${PRICING.AE.website} in the UAE. The final price depends on the number of pages, features and integrations, and you get it in writing before any work starts.`,
  },
  {
    q: "What is the monthly growth plan?",
    a: `An optional subscription from ${PRICING.IN.growth} (India), ${PRICING.US.growth} (USA) or ${PRICING.AE.growth} (UAE) a month. It covers SEO improvements, new articles and landing pages, website updates and conversion improvements, so the site keeps bringing more enquiries.`,
  },
  {
    q: "Do I have to take the monthly plan?",
    a: "No. The website is yours from day one with no lock-in. Many clients start with the website alone and add the growth plan once it is live.",
  },
  {
    q: "Why are your prices lower than agencies in the USA and Dubai?",
    a: "Grotech Web works remotely from India, without large agency overheads. You work directly with the people building your site, and get the same standards of design, speed and SEO.",
  },
  {
    q: "How do I get an exact quote?",
    a: "Send a message on WhatsApp or fill in the form with what your business does and what you need. You get a custom plan with scope, timeline and a fixed price.",
  },
];

const PLANS = ["IN", "US", "AE"];

export default function Pricing() {
  const schema = [
    {
      "@type": "OfferCatalog",
      "@id": "https://grotechweb.com/pricing/#catalog",
      name: "Grotech Web pricing",
      itemListElement: PLANS.flatMap((code) => {
        const p = PRICING[code];
        const currency = code === "IN" ? "INR" : code === "US" ? "USD" : "AED";
        const num = (v) => Number(v.replace(/[^\d]/g, ""));
        return [
          { "@type": "Offer", name: `Business website (${p.country})`, seller: { "@id": ORG_ID }, areaServed: { "@type": "Country", name: p.country }, priceSpecification: { "@type": "PriceSpecification", minPrice: num(p.website), priceCurrency: currency } },
          { "@type": "Offer", name: `Monthly growth plan (${p.country})`, seller: { "@id": ORG_ID }, areaServed: { "@type": "Country", name: p.country }, priceSpecification: { "@type": "UnitPriceSpecification", minPrice: num(p.growth), priceCurrency: currency, unitText: "MONTH" } },
        ];
      }),
    },
    faqSchema(FAQS),
  ];

  return (
    <main className="pt-36 lg:pt-44" data-testid="pricing-page">
      <SEO path="/pricing" schema={schema} breadcrumbs={[{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }]} />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Chapter index="—" label="Pricing" />
        <h1 className="mt-8 max-w-4xl font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLine>Website Development Pricing for India, USA & UAE</MaskedLine>
        </h1>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/60 sm:text-lg">
          Start with a premium, SEO-ready website at an affordable one-time cost. Add the optional monthly growth plan when you want more traffic and more enquiries. Every price below is a starting point; you get a fixed quote in your currency before any work begins.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16" data-testid="pricing-plans">
        <div className="grid gap-5 lg:grid-cols-3">
          {PLANS.map((code) => {
            const p = PRICING[code];
            return (
              <div key={code} className="flex flex-col rounded-3xl border border-white/10 bg-ink p-8" data-testid={`pricing-${code}`}>
                <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-ember">{p.label}</h2>
                <p className="mt-6 text-sm text-white/50">Website build</p>
                <p className="font-display text-4xl font-black">from {p.website}</p>
                <p className="mt-5 text-sm text-white/50">Monthly growth plan (optional)</p>
                <p className="font-display text-2xl font-bold">from {p.growth}<span className="text-base text-white/50">/month</span></p>
                <p className="mt-5 flex-1 text-xs leading-relaxed text-white/45">{p.note}</p>
                <button
                  onClick={() => { track("cta_click", { location: "pricing", market: code }); openLeadPopup(); }}
                  className="mt-7 flex items-center justify-center gap-2 rounded-full bg-ember px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
                >
                  Get My Fixed Quote <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:px-16">
        <div className="grid gap-10 md:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Every website includes</h2>
            <ul className="mt-6 space-y-3">
              {WEBSITE_INCLUDES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-white/70"><Check className="mt-0.5 h-5 w-5 shrink-0 text-ember" />{f}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">The growth plan adds</h2>
            <ul className="mt-6 space-y-3">
              {GROWTH_INCLUDES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-white/70"><Check className="mt-0.5 h-5 w-5 shrink-0 text-ember" />{f}</li>
              ))}
            </ul>
          </Reveal>
        </div>
        <p className="mt-10 text-sm text-white/55">
          Need a store or AI? See{" "}
          <Link to="/shopify-website-development/" className="text-ember underline-offset-2 hover:underline">Shopify development</Link>,{" "}
          <Link to="/ai-chatbot-development/" className="text-ember underline-offset-2 hover:underline">AI chatbots</Link> and{" "}
          <Link to="/seo-services/" className="text-ember underline-offset-2 hover:underline">SEO services</Link>, or browse{" "}
          <Link to="/projects/" className="text-ember underline-offset-2 hover:underline">recent work</Link>.
        </p>
      </section>

      <section className="border-t border-line bg-ink py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <Chapter index="?" label="Pricing questions" />
          <div className="mt-8 space-y-4">
            {FAQS.map((f) => (
              <details key={f.q} className="rounded-2xl border border-white/10 p-6">
                <summary className="cursor-pointer font-display text-base font-semibold text-white">{f.q}</summary>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap gap-4">
            <button
              onClick={() => { track("cta_click", { location: "pricing_bottom" }); openLeadPopup(); }}
              className="flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
            >
              Get My Fixed Quote <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href={waLink("Hi Grotech Web! I saw your pricing and would like a quote for my business.")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { location: "pricing" })}
              className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
            >
              <MessageCircle className="h-4 w-4" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
